import React, { useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { faPlus, faMinus, faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { useMap } from 'react-map-gl';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import bbox  from '@turf/bbox';

import { FontAwesomeSvgIcon } from 'handimap-react-lib/components/FontAwesomeSvgIcon';
import { useResponsive } from 'handimap-react-lib/hooks/useResponsive';
import { getGeolocationPermission } from 'handimap-react-lib/hooks/useGeoLocation';
import { setAlert } from 'handimap-react-lib/store/actions/alertActions';
import { showGeoLocation } from 'handimap-react-lib/store/actions/mapActions';
import { getRadiusOfCenter } from 'handimap-react-lib/utils/useGeoJson';
import { hideGeoLocation } from 'handimap-react-lib/store/actions/mapActions';
import { useSelector } from 'react-redux';

export const MapControlComponent = () => {

  const { mymap } = useMap();
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const showGeo = useSelector(state => state.map.showGeoLocation);

  const handleZoomIn = () => {
    if (mymap) {
      mymap.zoomIn({duration: 500});
    }
  };
    
  const handleZoomOut = () => {
    if (mymap) {
      mymap.zoomOut({duration: 500});
    }
  };

  const handleGeoLocation = useCallback(() => {
    getGeolocationPermission().then(res => {
      if (res.state === 'denied') {
        dispatch(setAlert(t('error.error-geolocation'), 'error',  t('error.label'), 'alert'));
      } else  {
        navigator.geolocation.getCurrentPosition( pos => {
          const value = {longitude: pos.coords.longitude, latitude: pos.coords.latitude};
          if (value !== null && typeof value === 'object') {
            const center = value.longitude && value.latitude ? [value.longitude, value.latitude] : null;
            dispatch(showGeoLocation(center));
            const radiusUserLocation = getRadiusOfCenter(center);
            const bounds = bbox(radiusUserLocation);
            if (bounds !== undefined) {
              mymap.getMap().fitBounds(bounds);
            } else {
              mymap.getMap().flyTo({
                center: center,
                zoom: 15
              });  
            }
          } else {
            dispatch(setAlert(t('error.error-place-origin-feature'), 'error',  t('error.label'), 'alert'));
          }
        }, (error) => {
          if(error.code == 1) {
            // Error: Access is denied!
            dispatch(setAlert(t('error.error-geolocation'), 'error',  t('error.label'), 'alert'));
          } else if( error.code == 2) {
            // "Error: Position is unavailable!"
            dispatch(setAlert(t('error.error-place-origin-feature'), 'error',  t('error.label'), 'alert'));
          }
        });
      }
    });
  }, []);

  const handleDeactivateGeoControl = useCallback(() => {
    dispatch(hideGeoLocation());
  }, []);

  const styles = {
    container : {
      display: 'flex',    
    },
    zoomContainer: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#FFFFFF',
      borderRadius: '.5em',
      width: isMobile ? '2em' : '3em',
      paddingX: '.3em',
      paddingY: '.5em'
    },
    divide: {
      display: 'flex',
      width: '100%',
      height: '2px',
      backgroundColor: '#D3D3D3',
      marginY: '.3em'
    },
    geolocateContainer: {
      backgroundColor: showGeo ? 'primary.main' : '#FFFFFF',
      borderRadius: '.5em',
      paddingX: '.3em',
      paddingY: '.5em',
      width: isMobile ? '2em' : '3em',
      display: 'flex',
      flexDirection: 'column',
    },
    icon: {
      fontSize: 22,
      fontWeight: 700,
      color: '#000000'
    },
    button: {
      '&:hover': {
        backgroundColor: '#FFFFFF'
      }
    },
    buttonGeo: {
      backgroundColor: showGeo ? 'primary.main' : '#FFFFFF',
      '&:hover': {
        backgroundColor: 'transparent'
      }
    }
  };
  return (
    <Stack sx={styles.container} direction='column' spacing={2}>
      <Box sx={styles.geolocateContainer}>
        <IconButton 
          sx={styles.buttonGeo} 
          id='geolocate-button' 
          aria-label={t('common.aria_button_geolocate')}
          onClick={showGeo ? handleDeactivateGeoControl : handleGeoLocation}
        >
          <FontAwesomeSvgIcon sx={styles.icon} icon={faCrosshairs} />
        </IconButton>
      </Box>
      <Box sx={styles.zoomContainer}>
        <IconButton 
          sx={styles.button}
          id='zoom-in-button'
          aria-label={t('common.aria_button_zoom_in')}
          onClick={handleZoomIn}
        >
          <FontAwesomeSvgIcon sx={styles.icon} icon={faPlus} />
        </IconButton>
        <Box sx={styles.divide} />
        <IconButton 
          sx={styles.button} 
          id='zoom-out-button' 
          aria-label={t('common.aria_button_zoom_out')}
          onClick={handleZoomOut}
        >
          <FontAwesomeSvgIcon sx={styles.icon} icon={faMinus} />
        </IconButton>
      </Box>
    </Stack>
  );
};